import React from 'react';
import { AreaChart, Area, ResponsiveContainer } from 'recharts';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { cn } from '@/lib/utils';
import { CU2_TOKENS } from '@/tokens';
import type { KpiData } from '@/types/dashboard';

interface KpiCardProps {
  data: KpiData;
  className?: string;
}

export function KpiCard({ data, className }: KpiCardProps) {
  const deltaColor =
    data.deltaDirection === 'up'
      ? 'text-pass'
      : data.deltaDirection === 'down'
        ? 'text-fail'
        : 'text-ink-muted';

  const DeltaIcon =
    data.deltaDirection === 'up'
      ? TrendingUp
      : data.deltaDirection === 'down'
        ? TrendingDown
        : Minus;

  const deltaSign = data.delta > 0 ? '+' : '';

  return (
    <div
      className={cn(
        'bg-white rounded-xl border border-line shadow-sm p-4',
        className,
      )}
    >
      {/* Label */}
      <p className="text-xs font-medium text-ink-muted uppercase tracking-wide mb-1">
        {data.label}
      </p>

      {/* Value + Delta */}
      <div className="flex items-center gap-2 mb-3">
        <span className="text-2xl font-bold text-ink font-heading">
          {data.value}
        </span>
        <span className={cn('inline-flex items-center gap-0.5 text-xs font-medium', deltaColor)}>
          <DeltaIcon className="h-3.5 w-3.5" />
          {deltaSign}{data.delta}%
        </span>
      </div>

      {/* Sparkline */}
      <ResponsiveContainer width="100%" height={40}>
        <AreaChart data={data.sparkData}>
          <Area
            type="monotone"
            dataKey="value"
            stroke={CU2_TOKENS.blue}
            fill={CU2_TOKENS.blue}
            fillOpacity={0.1}
            strokeWidth={1.5}
            dot={false}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}
